import { useEffect, useState } from "react";

type Look = { fit: "contain" | "cover"; bg: string | null };
const cache = new Map<string, Look>();
const SAFE: Look = { fit: "contain", bg: null };

/**
 * Studio packshots (product on a plain light background) must be shown whole
 * ("contain"); lifestyle photos fill the frame ("cover"). Decided by sampling
 * the four corners of the image: all light and nearly equal → studio shot.
 * For studio shots the corner colour is also returned, so the frame around
 * the photo can be painted the same colour and the photo has no visible edge.
 */
export function useImageLook(src: string | null | undefined): Look {
  const [look, setLook] = useState<Look>(() => (src && cache.get(src)) || SAFE);
  useEffect(() => {
    if (!src) return;
    const known = cache.get(src);
    if (known) return setLook(known);
    setLook(SAFE);
    let alive = true;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = c.height = 24;
        const ctx = c.getContext("2d", { willReadFrequently: true });
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, 24, 24);
        const px = (x: number, y: number) => ctx.getImageData(x, y, 1, 1).data;
        const corners = [px(1, 1), px(22, 1), px(1, 22), px(22, 22)];
        const light = corners.every((d) => d[0] > 214 && d[1] > 214 && d[2] > 210);
        const spread = Math.max(...corners.map((d) => d[0] + d[1] + d[2])) - Math.min(...corners.map((d) => d[0] + d[1] + d[2]));
        const studio = light && spread < 60;
        const avg = (i: number) => Math.round(corners.reduce((s, d) => s + d[i], 0) / corners.length);
        const result: Look = { fit: studio ? "contain" : "cover", bg: studio ? `rgb(${avg(0)}, ${avg(1)}, ${avg(2)})` : null };
        cache.set(src, result);
        if (alive) setLook(result);
      } catch {
        /* unreadable (cross-origin) → keep the safe "contain" */
      }
    };
    img.src = src;
    return () => {
      alive = false;
    };
  }, [src]);
  return look;
}

export function useImageFit(src: string | null | undefined): "contain" | "cover" {
  return useImageLook(src).fit;
}
